import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ErrorCard, isChunkError, reloadForStaleBuild } from '@/components/ChunkErrorBoundary';

/**
 * The boundary around EVERYTHING — the one that stops a white page.
 *
 * React unmounts the whole tree when a render throws and nothing above it
 * catches: that is not a crash screen, it is `<div id="root">` left empty,
 * and on a phone it is indistinguishable from the app failing to load at all.
 * The route boundaries (ChunkErrorBoundary, keyed per location) catch a PAGE
 * that throws; nothing caught the shell itself — the sidebar, the header, a
 * provider, the prompt cards — so a crash there was exactly that white page.
 *
 * Mount it outermost in App.tsx, around every provider. That placement is why
 * its card uses no hook of any kind: the theme, language and query providers
 * may be the very thing that threw. It still looks like SaleFish — index.css
 * is loaded and theme-init.js has already put the light/dark class on <html>
 * before first paint.
 *
 * A stale-build chunk error reloads once, on the same 30-second guard as the
 * route boundary, so the two can never loop between them.
 */
interface State {
  error: unknown;
  reloading: boolean;
}

export class AppErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null, reloading: false };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { error: error ?? new Error('Unknown render error') };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[AppErrorBoundary] The app shell failed to render:', error, '\nComponent stack:', info.componentStack);
    if (isChunkError(error) && reloadForStaleBuild()) this.setState({ reloading: true });
  }

  render() {
    const { error, reloading } = this.state;
    if (!error) return this.props.children;
    if (reloading) return <div className="min-h-dvh" />;
    return (
      <main className="flex min-h-dvh items-center justify-center px-4 py-10">
        {/* No `t`: this renders above the language and query providers, so the
            card stays English (see ErrorCard). */}
        <ErrorCard error={error} scope="app" className="w-full text-center" />
      </main>
    );
  }
}
